import { Alert, Flex, Heading, InlineList, Text, View } from "@instructure/ui";
import c3 from "c3";
import { t } from "i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ExternalService } from "../../../../../services/ExternalService";
import ChartColors from "../../../../../utils/ChartColors";
import { sort } from "../../../../../utils/utils";
import { CANVAS, EXTERNAL } from "../../../Plan/utils";

const AssessmentChart = ({ measure, filters, triggerMap }) => {
  const [title, setTitle] = useState("");
  const [loadingChart, setLoadingChart] = useState(true);
  const [error, setError] = useState(false);
  const [noData, setNoData] = useState(false);
  const [graphData, setGraphData] = useState(null);

  useEffect(() => {
    const getGraphData = async () => {
      try {
        setLoadingChart(true);
        if (measure.assignment_evidence.length > 0) {
          setTitle(
            filters.masteryResults === "true"
              ? t("assessment.masteryAvg")
              : t("assessment.gradeAvg")
          );
          let context = "";
          if (measure.type === CANVAS) {
            context = "canvas_course_embedded";
          } else if (measure.type === EXTERNAL) {
            context = "external_assessment";
          }
          const results = await ExternalService.getAssessmentResults(
            measure.assignment_evidence,
            filters.mode,
            filters.masteryResults,
            context
          );

          if (results[0].length === 0) {
            setNoData(true);
          } else {
            setNoData(false);
            results[0].forEach((term) => {
              for (const key in term) {
                if (key !== "name" && term[key] !== null) {
                  term[key] = Number(term[key].toFixed(2));
                }
              }
            });
            results[1] = sort(results[1]);
            setGraphData(results);
          }
        } else {
          setNoData(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoadingChart(false);
      }
    };
    getGraphData();
  }, [measure, triggerMap]);

  useEffect(() => {
    if (graphData) {
      c3.generate({
        bindto: document.getElementById("assessment-chart-" + measure.id),
        padding: { bottom: 25, left: 50 },
        color: { pattern: ChartColors },
        legend: { show: false },
        size: { height: 240 },
        data: {
          type: "bar",
          json: graphData[0],
          keys: { x: "name", value: graphData[1] },
        },
        zoom: { enabled: true },
        axis: {
          x: {
            type: "category", // this needed to load string x value
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
          },
        },
        tooltip: {
          order: function (data1, data2) {
            return sort([data1, data2], (d) => d.name);
          },
        },
      });
    }
  }, [graphData, measure.id]);

  const chartTitle = (measure) => {
    if (measure.type === EXTERNAL) {
      return t("assessment.externalResults");
    } else if (measure.type === CANVAS) {
      return t("results.targetedCanvasAssessment");
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{chartTitle(measure)}</Heading>
      </Flex.Item>
      {measure.description && (
        <Flex.Item padding="0 0 0 large">
          <Text>{measure.description}</Text>
        </Flex.Item>
      )}
      {loadingChart ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Skeleton style={{ height: 300, borderRadius: 5 }} />
        </Flex.Item>
      ) : error ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="error">
            {t("notifications.general.fetchError", {
              obj: t("results.assessmentResults"),
            })}
          </Alert>
        </Flex.Item>
      ) : noData ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("assessment.noResultsFound")}</Alert>
        </Flex.Item>
      ) : (
        <>
          <Flex.Item size="100%" padding="small 0 0 large">
            <View textAlign="center">
              <Heading level="h4">{title}</Heading>
              <div
                id={"assessment-chart-" + measure.id}
                style={{ margin: "10px 0 -20px 0", width: "100%" }}
              />
            </View>
          </Flex.Item>
          <Flex.Item size="100%" padding="0 0 medium xx-large">
            <View as="div" textAlign="center">
              <InlineList delimiter="pipe" isUnstyled>
                {graphData[1].map((category, index) => (
                  <InlineList.Item key={index}>
                    <div
                      style={{
                        display: "inline-block",
                        height: 10,
                        width: 10,
                        marginRight: 8,
                        backgroundColor: ChartColors[index],
                      }}
                    />
                    <Text as="span" size="small">
                      {category}
                    </Text>
                  </InlineList.Item>
                ))}
              </InlineList>
            </View>
          </Flex.Item>
        </>
      )}
    </Flex>
  );
};

export default AssessmentChart;
